import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type RefObject,
} from 'react';
import { ArrowRight, X } from 'lucide-react';
import type { Reconciliation } from '@molt/shared';
import { Button, Dialog, EmptyState, IconButton, Notice, TextField } from '@/components';
import {
  cardMoveNeighbours,
  hasReconciliationConsequences,
  type CardMovePlacement,
} from '@/lib/cross-board-move';
import {
  useInterfaceLanguage,
  type InterfaceLanguageContextValue,
} from '@/lib/interface-language';
import { MoveReconciliationReport } from './MoveReconciliationReport';

export interface DestinationBoardOption {
  readonly id: string;
  readonly title: string;
  readonly workspaceId: string;
  readonly workspaceName: string;
}

export interface DestinationListOption {
  readonly id: string;
  readonly boardId: string;
  readonly title: string;
}

export interface CrossBoardMoveDialogProps {
  open: boolean;
  /**
   * Focused on close instead of the element that opened the dialog, for an
   * opener that no longer exists by then: the card back opens this from an
   * item of its "⋯" Actions menu, and hands focus back to the menu trigger.
   */
  returnFocus?: RefObject<HTMLElement | null> | undefined;
  mode: 'card' | 'list';
  subjectTitle: string;
  currentBoardId: string;
  currentListId?: string | undefined;
  currentListTitle?: string | undefined;
  currentCardId?: string | undefined;
  currentCards?: readonly { id: string; title: string }[] | undefined;
  cardsByListId?: ReadonlyMap<string, readonly { id: string; title: string }[]>;
  boards: readonly DestinationBoardOption[];
  listsByBoardId: ReadonlyMap<string, readonly DestinationListOption[]>;
  loading?: boolean | undefined;
  busy?: boolean | undefined;
  result?: Reconciliation | null | undefined;
  cardsMoved?: number | undefined;
  onBoardNeedsLists?: ((boardId: string) => void) | undefined;
  onCancel: () => void;
  onPreviewCard?:
    | ((input: {
        boardId: string;
        listId: string;
        previousCardId: string | null;
        nextCardId: string | null;
      }) => Promise<Reconciliation>)
    | undefined;
  onPreviewList?: ((input: { boardId: string }) => Promise<Reconciliation>) | undefined;
  onMoveCard?:
    | ((input: {
        boardId: string;
        listId: string;
        previousCardId: string | null;
        nextCardId: string | null;
        sameBoard: boolean;
      }) => void)
    | undefined;
  onMoveList?: ((input: { boardId: string }) => void) | undefined;
}

export function CrossBoardMoveDialog({
  open,
  mode,
  subjectTitle,
  currentBoardId,
  currentListId,
  currentListTitle: _currentListTitle,
  currentCardId,
  currentCards,
  cardsByListId,
  boards,
  listsByBoardId,
  loading,
  busy,
  result,
  cardsMoved,
  onBoardNeedsLists,
  onCancel,
  onPreviewCard,
  onPreviewList,
  onMoveCard,
  onMoveList,
  returnFocus,
}: CrossBoardMoveDialogProps): ReactElement | null {
  const { t } = useInterfaceLanguage();
  const boardSelectId = useId();
  const listSelectId = useId();
  const searchField = useRef<HTMLInputElement | null>(null);
  const [query, setQuery] = useState('');
  const [selectedBoardId, setSelectedBoardId] = useState('');
  const [selectedListId, setSelectedListId] = useState('');
  const [placement, setPlacement] = useState<CardMovePlacement>('bottom');
  const [preview, setPreview] = useState<{
    key: string;
    reconciliation: Reconciliation;
  } | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const filteredBoards = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    if (needle === '') return boards;
    return boards.filter((board) =>
      `${board.title} ${board.workspaceName}`.toLocaleLowerCase().includes(needle),
    );
  }, [boards, query]);

  const defaultBoardId = useMemo(() => {
    if (mode === 'card' && boards.some((board) => board.id === currentBoardId)) {
      return currentBoardId;
    }

    const nonCurrentBoard = boards.find((board) => board.id !== currentBoardId);
    return nonCurrentBoard?.id ?? boards[0]?.id ?? '';
  }, [boards, currentBoardId, mode]);

  const visibleBoardId = filteredBoards.some((board) => board.id === selectedBoardId)
    ? selectedBoardId
    : (filteredBoards[0]?.id ?? '');

  const selectedBoard =
    filteredBoards.find((board) => board.id === visibleBoardId) ?? null;
  const availableLists =
    selectedBoard === null ? [] : (listsByBoardId.get(selectedBoard.id) ?? []);

  const defaultListId = useMemo(() => {
    if (
      mode === 'card' &&
      selectedBoard?.id === currentBoardId &&
      currentListId !== undefined &&
      availableLists.some((list) => list.id === currentListId)
    ) {
      return currentListId;
    }

    return availableLists[0]?.id ?? '';
  }, [availableLists, currentBoardId, currentListId, mode, selectedBoard?.id]);

  const effectiveListId = availableLists.some((list) => list.id === selectedListId)
    ? selectedListId
    : defaultListId;

  const positionCards =
    mode === 'card'
      ? (cardsByListId?.get(effectiveListId) ??
        (selectedBoard?.id === currentBoardId && effectiveListId === currentListId
          ? (currentCards ?? [])
          : []))
      : [];

  const placementChoices = useMemo(
    () => buildPlacementChoices(t, positionCards, currentCardId),
    [currentCardId, positionCards, t],
  );

  useEffect(() => {
    if (!open) {
      setQuery('');
      setSelectedBoardId('');
      setSelectedListId('');
      setPlacement('bottom');
      setPreview(null);
      setPreviewError(null);
      setPreviewing(false);
      return;
    }

    setSelectedBoardId(defaultBoardId);
  }, [defaultBoardId, open]);

  useEffect(() => {
    if (!open || mode !== 'card') return;
    setSelectedListId(defaultListId);
  }, [defaultListId, mode, open]);

  useEffect(() => {
    if (!open || mode !== 'card') return;
    setPlacement(defaultPlacement(positionCards, currentCardId));
  }, [currentCardId, mode, open, positionCards]);

  if (!open) return null;

  const cleanTitle =
    subjectTitle.trim() === '' ? t('copyMove.untitledItem') : subjectTitle;
  const isCardMode = mode === 'card';
  const sameBoard = selectedBoard?.id === currentBoardId;
  const cardHasNoDestinationList = isCardMode && availableLists.length === 0;
  const confirmDisabled =
    busy === true ||
    loading === true ||
    selectedBoard === null ||
    (isCardMode && (cardHasNoDestinationList || effectiveListId === '')) ||
    (!isCardMode && selectedBoard.id === currentBoardId);

  const disabledReason =
    loading === true
      ? t('copyMove.loadingBoards')
      : selectedBoard === null
        ? t('copyMove.chooseBoardFirst')
        : isCardMode && cardHasNoDestinationList
          ? t('copyMove.move.needsList')
          : !isCardMode && selectedBoard.id === currentBoardId
            ? t('copyMove.move.differentBoard')
            : undefined;
  const busyButtonProps = busy === undefined ? {} : { busy };
  const disabledReasonProps = disabledReason === undefined ? {} : { disabledReason };
  const previewKey =
    selectedBoard === null
      ? ''
      : isCardMode
        ? [
            'card',
            selectedBoard.id,
            effectiveListId,
            placement,
            currentCardId ?? '',
          ].join(':')
        : ['list', selectedBoard.id].join(':');
  const showPreview =
    preview !== null &&
    preview.key === previewKey &&
    hasReconciliationConsequences(preview.reconciliation);

  async function submitMove() {
    if (selectedBoard === null) return;

    if (isCardMode) {
      if (effectiveListId === '') return;
      const cardsForNeighbours = positionCards.filter(
        (card) => card.id !== currentCardId,
      );
      const neighbours = cardMoveNeighbours(cardsForNeighbours, placement);
      const cardInput = {
        boardId: selectedBoard.id,
        listId: effectiveListId,
        previousCardId: neighbours.previousCardId,
        nextCardId: neighbours.nextCardId,
        sameBoard,
      };
      if (!sameBoard && !showPreview && onPreviewCard !== undefined) {
        setPreviewing(true);
        setPreviewError(null);
        try {
          const reconciliation = await onPreviewCard({
            boardId: cardInput.boardId,
            listId: cardInput.listId,
            previousCardId: cardInput.previousCardId,
            nextCardId: cardInput.nextCardId,
          });
          setPreview({ key: previewKey, reconciliation });
          if (hasReconciliationConsequences(reconciliation)) return;
        } catch {
          setPreviewError(t('copyMove.move.previewFailed'));
          return;
        } finally {
          setPreviewing(false);
        }
      }
      onMoveCard?.({
        boardId: cardInput.boardId,
        listId: cardInput.listId,
        previousCardId: cardInput.previousCardId,
        nextCardId: cardInput.nextCardId,
        sameBoard: cardInput.sameBoard,
      });
      return;
    }

    if (selectedBoard.id !== currentBoardId) {
      if (!showPreview && onPreviewList !== undefined) {
        setPreviewing(true);
        setPreviewError(null);
        try {
          const reconciliation = await onPreviewList({ boardId: selectedBoard.id });
          setPreview({ key: previewKey, reconciliation });
          if (hasReconciliationConsequences(reconciliation)) return;
        } catch {
          setPreviewError(t('copyMove.move.previewFailed'));
          return;
        } finally {
          setPreviewing(false);
        }
      }
      onMoveList?.({ boardId: selectedBoard.id });
    }
  }

  return (
    <Dialog
      {...(returnFocus ? { returnFocus } : {})}
      open
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onCancel();
      }}
      size="md"
      title={
        isCardMode
          ? t('copyMove.move.cardTitle', { title: cleanTitle })
          : t('copyMove.move.listTitle', { title: cleanTitle })
      }
      description={
        isCardMode
          ? t('copyMove.move.cardDescription')
          : t('copyMove.move.listDescription')
      }
      initialFocus={searchField}
      closeControl={
        <IconButton label={t('copyMove.move.close')} size="sm" variant="quiet">
          <X aria-hidden="true" className="size-4" />
        </IconButton>
      }
      footer={
        <>
          <Button size="sm" variant="secondary" onClick={onCancel}>
            {t('copyMove.cancel')}
          </Button>
          <Button
            size="sm"
            variant="primary"
            busyLabel={
              isCardMode ? t('copyMove.move.busyCard') : t('copyMove.move.busyList')
            }
            disabled={confirmDisabled || previewing}
            iconStart={<ArrowRight aria-hidden="true" className="size-4" />}
            onClick={() => void submitMove()}
            {...(previewing ? { busy: true } : busyButtonProps)}
            {...disabledReasonProps}
          >
            {isCardMode ? t('copyMove.move.submitCard') : t('copyMove.move.submitList')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {result ? (
          <MoveReconciliationReport
            reconciliation={result}
            cardsMoved={cardsMoved}
            tone="result"
          />
        ) : null}

        {showPreview ? (
          <MoveReconciliationReport
            reconciliation={preview.reconciliation}
            summary={t('copyMove.move.previewSummary')}
            tone="notice"
          />
        ) : null}

        {previewError !== null ? (
          <Notice
            tone="bad"
            title={t('copyMove.move.previewUnavailable')}
            body={previewError}
          />
        ) : null}

        <TextField
          ref={searchField}
          label={t('copyMove.move.search')}
          value={query}
          onChange={(event) => setQuery(event.currentTarget.value)}
          autoComplete="off"
          clearable
          onClear={() => setQuery('')}
        />

        {boards.length === 0 ? (
          <EmptyState
            tone="neutral"
            headingLevel={3}
            title={t('copyMove.noBoards.title')}
            body={t('copyMove.noBoards.body')}
          />
        ) : filteredBoards.length === 0 ? (
          <EmptyState
            tone="neutral"
            headingLevel={3}
            title={t('copyMove.move.noMatchTitle')}
            body={t('copyMove.move.noMatchBody')}
          />
        ) : (
          <>
            <div className="flex flex-col gap-1">
              <label htmlFor={boardSelectId} className="text-sm font-medium text-ink">
                {t('copyMove.destinationBoardLabel')}
              </label>
              <select
                id={boardSelectId}
                value={selectedBoard?.id ?? ''}
                onChange={(event) => {
                  setSelectedBoardId(event.currentTarget.value);
                  setSelectedListId('');
                  setPlacement('bottom');
                }}
                className="h-9 rounded-card border border-border-strong bg-surface-raised px-3 text-card text-ink focus:border-accent"
              >
                {filteredBoards.map((board) => (
                  <option key={board.id} value={board.id}>
                    {t('copyMove.boardOption', {
                      board: board.title,
                      workspace: board.workspaceName,
                    })}
                  </option>
                ))}
              </select>
            </div>

            {isCardMode ? (
              <>
                {cardHasNoDestinationList && selectedBoard !== null ? (
                  <EmptyState
                    tone="neutral"
                    headingLevel={3}
                    title={t('copyMove.noOpenLists')}
                    body={t('copyMove.move.noListsBody', {
                      board: selectedBoard.title,
                    })}
                    action={
                      onBoardNeedsLists ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => onBoardNeedsLists(selectedBoard.id)}
                        >
                          {t('copyMove.move.createList')}
                        </Button>
                      ) : undefined
                    }
                  />
                ) : (
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor={listSelectId}
                      className="text-sm font-medium text-ink"
                    >
                      {t('copyMove.destinationListLabel')}
                    </label>
                    <select
                      id={listSelectId}
                      value={effectiveListId}
                      onChange={(event) => {
                        setSelectedListId(event.currentTarget.value);
                        setPlacement('bottom');
                      }}
                      className="h-9 rounded-card border border-border-strong bg-surface-raised px-3 text-card text-ink focus:border-accent"
                    >
                      {availableLists.map((list) => (
                        <option key={list.id} value={list.id}>
                          {list.title}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {!cardHasNoDestinationList ? (
                  <fieldset className="flex flex-col gap-2">
                    <legend className="text-sm font-medium text-ink">
                      {t('copyMove.positionLabel')}
                    </legend>
                    <div className="flex flex-col gap-1">
                      {placementChoices.map((choice) => (
                        <label
                          key={choice.value}
                          className="flex items-center gap-2 rounded-card px-2 py-1 text-sm text-ink hover:bg-surface-raised"
                        >
                          <input
                            type="radio"
                            name="card-move-placement"
                            value={choice.value}
                            checked={placement === choice.value}
                            onChange={() => setPlacement(choice.value)}
                          />
                          <span>{choice.label}</span>
                        </label>
                      ))}
                    </div>
                  </fieldset>
                ) : null}
              </>
            ) : (
              <p className="text-sm text-ink-muted">{t('copyMove.move.listAppended')}</p>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
}

/**
 * ⚠ `t` first and required — a module helper cannot call the hook.
 *
 * ⚠ NO `Untitled card` FALLBACK HERE, and that is byte preservation rather than
 * an oversight: this helper always printed the raw card title, where
 * `CardCopyDialog`'s equivalent replaced a blank one. Giving it the leaf would
 * change what the dialog renders, which this slice does not do.
 */
function buildPlacementChoices(
  t: InterfaceLanguageContextValue['t'],
  cards: readonly { id: string; title: string }[],
  currentCardId: string | undefined,
): readonly { value: CardMovePlacement; label: string }[] {
  const otherCards = cards.filter((card) => card.id !== currentCardId);
  return [
    { value: 'top', label: t('copyMove.position.topOfList') },
    { value: 'bottom', label: t('copyMove.position.bottomOfList') },
    ...otherCards.map((card) => ({
      value: `after:${card.id}` as const,
      label: t('copyMove.position.afterCard', { card: card.title }),
    })),
  ];
}

function defaultPlacement(
  cards: readonly { id: string }[],
  currentCardId: string | undefined,
): CardMovePlacement {
  if (currentCardId === undefined) return 'bottom';

  const currentIndex = cards.findIndex((card) => card.id === currentCardId);
  if (currentIndex === -1) return 'bottom';
  if (currentIndex === 0) return 'top';

  return `after:${cards[currentIndex - 1]!.id}`;
}
